import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../auth';

const API = import.meta.env.VITE_API_URL || 'http://localhost:8000';

interface TechniqueRecord {
  viewed: boolean;
  completed: boolean;
  viewed_at: string;
  completed_at: string | null;
}

type ProgressMap = Record<string, TechniqueRecord>;

interface UseProgressReturn {
  progress: ProgressMap;
  completedIds: Set<string>;
  viewedIds: Set<string>;
  markViewed: (id: string) => void;
  markCompleted: (id: string) => void;
}

export function useProgress(): UseProgressReturn {
  const { token } = useAuth();
  const [progress, setProgress] = useState<ProgressMap>({});
  const pendingViews = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!token) return;
    fetch(`${API}/stats/techniques`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.progress) setProgress(data.progress);
      })
      .catch(() => {});
  }, [token]);

  const markViewed = useCallback(
    (id: string) => {
      if (!token) return;
      if (progress[id] || pendingViews.current.has(id)) return;
      pendingViews.current.add(id);
      setProgress((prev) => ({
        ...prev,
        [id]: {
          viewed: true,
          completed: false,
          viewed_at: new Date().toISOString(),
          completed_at: null,
        },
      }));
      fetch(`${API}/stats/techniques/${encodeURIComponent(id)}/view`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => {});
    },
    [token, progress],
  );

  const markCompleted = useCallback(
    (id: string) => {
      if (!token) return;
      if (progress[id]?.completed) return;
      const now = new Date().toISOString();
      setProgress((prev) => ({
        ...prev,
        [id]: {
          viewed: true,
          completed: true,
          viewed_at: prev[id]?.viewed_at ?? now,
          completed_at: now,
        },
      }));
      fetch(`${API}/stats/techniques/${encodeURIComponent(id)}/complete`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => {});
    },
    [token, progress],
  );

  const completedIds = new Set(
    Object.entries(progress)
      .filter(([, v]) => v.completed)
      .map(([k]) => k),
  );
  const viewedIds = new Set(Object.keys(progress));

  return { progress, completedIds, viewedIds, markViewed, markCompleted };
}
